import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { PutRequest } from "../../Utils/hooks/ApiHandler";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { toast } from "sonner";
import { getCountries, getStates, getCities } from "../../Utils/hooks/location";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";

export const PurchaseAddressModal = ({
  show,
  handleClose,
  billing_address,
  handleFetch,
  defaultFor,
}) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const extras = { customer, setCustomer, rf, navigate };
  const headers = {
    Authorization: token,
    Authentication: rf,
  };

  const [showAddForm, setShowAddForm] = useState(false);

  // ✅ Dynamic states
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const { register, handleSubmit, control, reset } = useForm();

  // ✅ Fetch countries on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await getCountries();
        if (!data) return;
        setCountries(
          data.map((country) => ({
            value: country.id,
            label: country.name,
          }))
        );
      } catch (error) {
        toast.error("Error fetching countries");
      }
    })();
  }, []);

  // ✅ Fetch states when country changes
  useEffect(() => {
    (async () => {
      if (!selectedCountry) {
        setStates([]);
        return;
      }
      try {
        const data = await getStates(selectedCountry.value);
        if (!data) return;
        setStates(
          data.map((state) => ({
            value: state.id,
            label: state.state_name,
          }))
        );
      } catch (error) {
        toast.error("Error fetching states");
      }
    })();
  }, [selectedCountry]);

  // ✅ Fetch cities when state changes
  useEffect(() => {
    (async () => {
      if (!selectedState) {
        setCities([]);
        return;
      }
      try {
        const data = await getCities(selectedState.value);
        if (!data) return;
        setCities(
          data.map((city) => ({
            value: city.id,
            label: city.city_name,
          }))
        );
      } catch (error) {
        toast.error("Error fetching cities");
      }
    })();
  }, [selectedState]);

  // ✅ addresses state
  const [addresses, setAddresses] = useState([]);
  useEffect(() => {
    setAddresses(billing_address)
  }, [defaultFor, billing_address]);

  // ✅ FIXED Select handler
  const handleSelectAddress = async (addr, selectedIndex) => {
    let updatedAddresses = [];

    if (defaultFor === "billing") {
      updatedAddresses = addresses.map((item, i) => ({
        ...item, // Keep all existing properties
        selected: i === selectedIndex ? "selected" : "", // Clear others, set selected
        for: item.for || "", // Preserve any existing shipping selection
      }));
    }

    if (defaultFor === "shipping") {
      updatedAddresses = addresses.map((item, i) => ({
        ...item, // Keep all existing properties
        selected: item.selected || "", // Preserve any existing billing selection
        for: i === selectedIndex ? "shipping" : "", // Clear others, set shipping
      }));
    }

    const payload = {
      customer_id: customer.customer_details?.customer_id,
      billing_address: updatedAddresses,
    };

    try {
      const res = await PutRequest(
        `/purchase/createBillingAddress/`,
        payload,
        { headers },
        extras
      );

      if (res.status === "success") {
        toast.success(res.message);

        let updatedList = [];
        try {
          updatedList = JSON.parse(res.data.billing_address);
        } catch (err) {
          updatedList = updatedAddresses; // Fallback to local data
        }

        setAddresses(updatedList);
        handleFetch(); // Refresh parent data
        handleClose(true);
      } else {
        toast.error(res.message || "Failed to update address");
      }
    } catch (error) {
      toast.error("Failed to update address");
    }
  };

  // ✅ Form submit
  const handleFormSubmit = async (data) => {
    const address_data = {
      ...data,
      country: selectedCountry?.label || "",
      state: selectedState?.label || "",
      city: selectedCity?.label || "",
      selected: defaultFor === "billing" ? "selected" : "",
      for: defaultFor === "shipping" ? "shipping" : "",
    }

    let previous_address = []
    if (defaultFor === "billing") {
      previous_address = billing_address.map((c) => ({ ...c, selected: "" }))
    } else {
      previous_address = billing_address.map((c) => ({ ...c, for: "" }))
    }

    const payload = {
      customer_id: customer.customer_details?.customer_id,
      billing_address: [...previous_address, address_data]
    }

    try {
      const res = await PutRequest('purchase/createBillingAddress/', payload, { headers }, extras)
      if (res.status === 'fail' || res.success === false) {
        toast.error(res.message)
        return
      }
      toast.success(res.message)
      reset();
      setSelectedCountry(null);
      setSelectedState(null);
      setSelectedCity(null);
      handleFetch()
      setShowAddForm(false);
    } catch (error) {
      toast.error("Failed to add address");
    }
  };

  const handleEditAddress = (addr, i) => {
    setEditIndex(i);
    setIsEditing(true);

    reset({
      address: addr.address,
      country: { label: addr.country, value: addr.country_id || "" },
      state: { label: addr.state, value: addr.state_id || "" },
      city: { label: addr.city, value: addr.city_id || "" },
      zipcode: addr.zipcode,
    });

    setSelectedCountry(addr.country ? { label: addr.country, value: addr.country_id || "" } : null);
    setSelectedState(addr.state ? { label: addr.state, value: addr.state_id || "" } : null);
    setSelectedCity(addr.city ? { label: addr.city, value: addr.city_id || "" } : null);

    setShowAddForm(true);
  };

  // ✅ Edit save handler
  const handleEditSave = async (data) => {
    if (editIndex === null) return;

    const updatedAddresses = addresses.map((item, i) =>
      i === editIndex
        ? {
          ...item, // Keep existing properties like selected/for
          address: data.address,
          country: selectedCountry?.label || data.country || "",
          state: selectedState?.label || data.state || "",
          city: selectedCity?.label || data.city || "",
          zipcode: data.zipcode,
        }
        : item
    );

    const payload = {
      customer_id: customer.customer_details?.customer_id,
      billing_address: updatedAddresses,
    };

    try {
      const res = await PutRequest(
        `/purchase/createBillingAddress/`,
        payload,
        { headers },
        extras
      );

      if (res.status === "success") {
        toast.success(res.message);

        let updatedList = [];
        try {
          updatedList = JSON.parse(res.data.billing_address);
        } catch (err) {
          updatedList = updatedAddresses;
        }

        setAddresses(updatedList);
        handleFetch();

        // Reset editing state
        setEditIndex(null);
        setIsEditing(false);
        setShowAddForm(false);
        reset();
        setSelectedCountry(null);
        setSelectedState(null);
        setSelectedCity(null);
      } else {
        toast.error(res.message || "Failed to update address");
      }
    } catch (error) {
      toast.error("Failed to update address");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {defaultFor === "billing" ? "Billing Addresses" : "Shipping Addresses"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!showAddForm ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Select Address</h6>
              <Button
                className="btn btn-default"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                + Add Address
              </Button>
            </div>
            {addresses?.length > 0 ? (
              addresses.map((addr, i) => (
                <div
                  key={i}
                  className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded"
                  style={{ cursor: "pointer" }}
                >
                  {/* Address Click Area */}
                  <div
                    className="d-flex align-items-center flex-grow-1"
                    onClick={() => handleSelectAddress(addr, i)}
                  >
                    {/* <Form.Check
                      type="radio"
                      name="selectedAddress"
                      value={i}
                      checked={
                        defaultFor === "billing"
                          ? addr.selected === "selected"
                          : addr.for === "shipping"
                      }
                      readOnly
                      className="me-2"
                      style={{ pointerEvents: 'none', width: "24px", height: "24px" }}
                    /> */}
                    <Form.Check
                      type="radio"
                      name="selectedAddress"
                      checked={
                        defaultFor === "billing"
                          ? addr.selected === "selected"
                          : addr.for === "shipping"
                      }
                      readOnly
                      style={{ width: "24px", height: '24px' }}
                      className="me-3 mt-1 custom-radio"
                    />
                    <div>
                      <p className="mb-0 fs-13 fw-500">{addr.address}</p>
                      <p className="mb-0 fs-13 text-dark">
                        {addr.city}, {addr.state}, {addr.country} - {addr.zipcode}
                      </p>
                    </div>
                  </div>

                  {/* Edit Button */}
                  <FontAwesomeIcon
                    icon={faPen}
                    style={{ color: "#6c757d", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAddress(addr, i);
                    }}
                  />
                </div>
              ))
            ) : (
              <p className="text-muted fs-13">No {defaultFor} address available</p>
            )}
          </>
        ) : (
          <Form onSubmit={handleSubmit(isEditing ? handleEditSave : handleFormSubmit)}>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Address"
                {...register("address", { required: true })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={countries}
                    placeholder="Select Country"
                    value={selectedCountry}
                    onChange={(val) => {
                      field.onChange(val);
                      setSelectedCountry(val);
                      setSelectedState(null);
                      setSelectedCity(null);
                      setCities([]);
                      setStates([]);
                    }}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={states}
                    placeholder="Select State"
                    value={selectedState}
                    onChange={(val) => {
                      field.onChange(val);
                      setSelectedState(val);
                      setSelectedCity(null);
                      setCities([]);
                    }}
                    isDisabled={!selectedCountry}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={cities}
                    placeholder="Select City"
                    value={selectedCity}
                    onChange={(val) => {
                      field.onChange(val);
                      setSelectedCity(val);
                    }}
                    isDisabled={!selectedState}
                  />
                )}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Zipcode</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Zipcode"
                {...register("zipcode", { required: true })}
              />
            </Form.Group>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setEditIndex(null);
                  setIsEditing(false);
                  reset();
                  setSelectedCountry(null);
                  setSelectedState(null);
                  setSelectedCity(null);
                }}
              >
                Back
              </Button>

              <Button type="submit" variant={isEditing ? "warning" : "primary"}>
                {isEditing ? "Save Edited Address" : "Save Address"}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal.Body>
      <style jsx>{`
        .custom-radio .form-check-input {
          width: 24px;
          height: 24px;
          background-color: var(--custom-white);
          border: 1px solid var(--input-border);
        }
        .custom-radio .form-check-input:checked {
          width: 24px;
          height: 24px;
          background-color: var(--primary-color);
          border-color: var(--primary-color);
        }
      `}</style>
    </Modal>

  );
};